import { Component, Element, Prop, h, forceUpdate } from "@stencil/core";
import {
    ContentDataProvider,
    getContentDataProvider,
} from "../../../apis/stateful-content";

import {
    getTranslateFn,
    TranslateFn,
    getTranslationSource,
    TranslationRenderType,
} from "../stateful-content-utils";

@Component({
    tag: "ux-iws-stateful-tiles",
    styleUrl: "stateful-tiles.scss",
    shadow: false,
})
export class MyTileContainer {
    private dataProvider: ContentDataProvider;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Content;
    private dataProviderUnsubscribe: () => void;

    @Element() domNode: HTMLUxIwsStatefulTilesElement;

    /** scope used to retrieve the content data provider */
    @Prop() scope: string = "";
    /** endpoint for content data */
    @Prop() url: string = "";
    /** optional translate function provided by parent to ensure consistent translations */
    @Prop() translateFn?: TranslateFn;

    componentWillLoad() {
        this.dataProvider = getContentDataProvider(this.scope);
        this.dataProviderUnsubscribe = this.dataProvider.subscribe(
            this.handleDataProviderState
        );
        if (this.url !== "") {
            this.dataProvider.setEndpoint(this.url);
        }
        this.setTranslations();
    }

    disconnectedCallback() {
        this.dataProviderUnsubscribe?.();
    }

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        // use translate function passed from stateful-overview to prevent loading conflicting translation sources while rendering progressively
        this.translate =
            this.translateFn ||
            getTranslateFn(
                getTranslationSource(
                    this.scope,
                    this.domNode,
                    this.translationRenderType
                )
            );
    };

    private getItemProps(item, data) {
        const serviceCaseCreationDisabled = data.isCreateCaseAllowed === false;

        const props = {
            "machine-name": item.name,
            "custom-machine-name": item.customName,
            "machine-image-url": item.image,
            "auth-image": data.authImages,
            "detail-link": item.detailLink,
            "toggle-fav-url": item.isFav.toggleUrl,
            "is-fav": item.isFav.active,
            createCaseMfaConfig: data.serviceCaseMicroFrontend || null,
            "sync-id": item.number,
            "order-tracking": item.orderTracking,
        };

        // Only add props that exist to avoid unnecessary attributes
        if (data.schema?.number?.type !== "hidden") {
            props["machine-id"] = item.number;
        }
        if (item.serviceCallModalLink) {
            props["service-call-modal-link"] = item.serviceCallModalLink;
        }
        if (serviceCaseCreationDisabled) {
            props["service-case-creation-disabled"] =
                serviceCaseCreationDisabled;
        }
        if (item.inProduction) {
            props["in-production"] = item.inProduction;
        }
        if (item.softwareUpdates) {
            props["software-updates"] = item.softwareUpdates;
        }
        if (item.shopLinks?.[0]?.link) {
            props["shop-link"] = item.shopLinks[0].link;
        }
        if (item.shopLinks?.[0]?.linkText) {
            props["shop-link-title"] = item.shopLinks[0].linkText;
        }
        if (item.shopLinks?.[0]?.shopType) {
            props["shop-link-type"] = item.shopLinks[0].shopType;
        }
        if (item.shopLinks?.[0]?.linkTarget) {
            props["shop-link-target"] = item.shopLinks[0].linkTarget;
        }
        if (item.shopLinks?.[1]?.link) {
            props["shop-link-2"] = item.shopLinks[1].link;
        }
        if (item.shopLinks?.[1]?.linkText) {
            props["shop-link-2-title"] = item.shopLinks[1].linkText;
        }
        if (item.shopLinks?.[1]?.shopType) {
            props["shop-link-2-type"] = item.shopLinks[1].shopType;
        }
        if (item.shopLinks?.[1]?.linkTarget) {
            props["shop-link-2-target"] = item.shopLinks[1].linkTarget;
        }
        if (item.chooseShopTitle) {
            props["choose-shop-title"] = item.chooseShopTitle;
        }

        return props;
    }

    private handleDataProviderState = () => {
        forceUpdate(this);
    };

    render() {
        if (
            this.dataProvider === undefined ||
            this.dataProvider.data === null
        ) {
            return null;
        }
        const { data } = this.dataProvider;

        return (
            <div class="ux-row ux-iws-stateful-overview__tile-container">
                {data.items.map((item) => {
                    const hasDiscontinuationInfo =
                        item.discontinued !== undefined;
                    return (
                        <ux-iws-tile
                            key={`${item.id || item.number}-${item.detailLink}-${item.checkboxValue}`}
                            class={`ux-col-12 ux-col-md-6 ux-col-xl-4 ux-u-mb-xs ${hasDiscontinuationInfo && "is-discontinued"}`}
                            {...this.getItemProps(item, data)}
                            translateFn={this.translate}
                        >
                            {hasDiscontinuationInfo && (
                                <div slot="discontinuation-icon">
                                    <ux-iws-discontinuation-icon
                                        tooltip-identifier={item.number}
                                        type={item.discontinued.type}
                                        status={item.discontinued.status}
                                        extended-support={
                                            item.discontinued.extendedSupport
                                        }
                                    ></ux-iws-discontinuation-icon>
                                    <ux-iws-discontinuation-tooltip
                                        tooltip-identifier={item.number}
                                        type={item.discontinued.type}
                                        status={item.discontinued.status}
                                        extended-support={
                                            item.discontinued.extendedSupport
                                        }
                                        head={item.discontinued.head}
                                        text={item.discontinued.text}
                                        link={item.discontinued.link}
                                        link-text={item.discontinued.linkText}
                                    ></ux-iws-discontinuation-tooltip>
                                </div>
                            )}
                        </ux-iws-tile>
                    );
                })}
            </div>
        );
    }
}
